import { useCallback, useEffect, useState } from "react";
import { Coins, Search } from "lucide-react";
import { fmtMYT } from "@/lib/myt";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const fmtCoins = (minor) => `${(Number(minor || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} COINS`;

export default function AdminCoins() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [member, setMember] = useState(null); // {id, full_name, uid, username}
  const [ledger, setLedger] = useState(null);
  const [form, setForm] = useState({ direction: "credit", amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [topups, setTopups] = useState(null);

  useEffect(() => {
    api.get("/admin/coins/topups").then((res) => setTopups(res.data.topups || [])).catch(() => setTopups([]));
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    try {
      const res = await api.get("/admin/members", { params: { q: q.trim() } });
      const list = Array.isArray(res.data) ? res.data : res.data.members || res.data.items || [];
      setResults(list.slice(0, 8));
    } catch { setResults([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  const loadLedger = useCallback(async (m) => {
    try {
      const res = await api.get("/admin/coins/ledger", { params: { user_id: m.id || m._id } });
      setLedger(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
      setLedger(null);
    }
  }, []);

  const pickMember = (m) => {
    const chosen = { id: m.id || m._id, full_name: m.full_name, uid: m.uid, username: m.username };
    setMember(chosen);
    setResults([]);
    setQuery("");
    loadLedger(chosen);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!member) return;
    const coins = Number(form.amount);
    if (!coins || coins <= 0) { setError("Enter a valid amount greater than zero."); return; }
    if (form.reason.trim().length < 3) { setError("A reason (min 3 characters) is mandatory for the audit trail."); return; }
    setBusy(true);
    try {
      await api.post("/admin/coins/adjust", {
        user_id: member.id,
        direction: form.direction,
        amount_minor: Math.round(coins * 100),
        reason: form.reason.trim(),
      });
      toast.success("Coins adjustment recorded");
      setForm({ direction: "credit", amount: "", reason: "" });
      loadLedger(member);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-coins-page">
      <div>
        <p className="eyebrow-label mb-2">Coin Management</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Coins Wallet Control</h1>
        <p className="mt-2 text-sm text-[#64748B]">
          1 COINS = USD 1. Manual adjustments are appended to an immutable audit ledger — history is never edited or deleted.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-8">
          <p className="eyebrow-label mb-6">Find Member</p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
            <input
              className="input-lux !pl-10 font-mono"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, UID (PM…) or username"
              data-testid="coin-member-search-input"
            />
          </div>
          {results.length > 0 && (
            <div className="mt-2 border border-[#222530] rounded-lg divide-y divide-[#222530] overflow-hidden" data-testid="coin-member-search-results">
              {results.map((m) => (
                <button key={m.id || m._id} onClick={() => pickMember(m)} className="w-full text-left px-4 py-3 hover:bg-[#0B0C10]/60 transition-colors" data-testid={`coin-member-option-${m.uid || m.username}`}>
                  <p className="text-sm text-white">{m.full_name}</p>
                  <p className="text-[10px] font-mono text-[#64748B]">{m.uid} · {m.username}</p>
                </button>
              ))}
            </div>
          )}
          {member && (
            <div className="mt-6 border border-[#D4AF37]/25 rounded-xl p-5 bg-[#D4AF37]/5" data-testid="coin-selected-member">
              <p className="text-sm text-white font-medium">{member.full_name}</p>
              <p className="text-[10px] font-mono text-[#64748B]">{member.uid} · {member.username}</p>
              <p className="mt-3 font-mono text-2xl font-bold text-[#D4AF37]" data-testid="coin-balance-display">
                {ledger ? fmtCoins(ledger.balance_minor) : "—"}
              </p>
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mt-1">Current Balance</p>
            </div>
          )}
        </div>

        <div className="card-lux p-8">
          <p className="eyebrow-label mb-6">Manual Adjustment</p>
          {!member ? (
            <p className="text-sm text-[#64748B]" data-testid="coin-adjust-no-member">Select a member first.</p>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="coin-adjust-form">
              <div className="flex gap-2">
                {[["credit", "CREDIT (+)"], ["debit", "DEBIT (−)"]].map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, direction: v }))}
                    className={`flex-1 text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-2 border transition-colors ${
                      form.direction === v
                        ? v === "credit" ? "text-black bg-emerald-400 border-emerald-400" : "text-black bg-red-400 border-red-400"
                        : "text-[#94A3B8] border-[#222530] hover:border-[#D4AF37]/30"
                    }`}
                    data-testid={`coin-direction-${v}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Amount (COINS)</label>
                <input type="number" min="0.01" step="0.01" className="input-lux font-mono" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="e.g. 50.00" required data-testid="coin-amount-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Reason <span className="text-red-400">*</span> <span className="text-[#64748B]">(mandatory — written to the audit trail)</span></label>
                <textarea className="input-lux min-h-[70px]" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Goodwill credit for support case #123" required data-testid="coin-reason-input" />
              </div>
              {error && <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="coin-adjust-error">{error}</p>}
              <button type="submit" disabled={busy} className="btn-gold px-8 py-3 text-sm disabled:opacity-60 flex items-center gap-2" data-testid="coin-adjust-submit">
                <Coins className="h-4 w-4" /> {busy ? "Recording..." : "Record Adjustment"}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="card-lux overflow-hidden" data-testid="admin-topups-section">
        <div className="px-6 pt-6">
          <p className="eyebrow-label">Recent Coin Purchases</p>
          <p className="mt-1 text-[11px] text-[#64748B]">Successful member COINS top-ups — newest first.</p>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="admin-topups-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Member</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">COINS</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Payment Ref</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {!topups ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#64748B]">Loading...</td></tr>
              ) : topups.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#64748B]" data-testid="admin-topups-empty">No coin purchases yet.</td></tr>
              ) : (
                topups.map((t) => (
                  <tr key={t.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`admin-topup-row-${t.id.slice(-6)}`}>
                    <td className="px-6 py-3">
                      <p className="text-sm text-white">{t.member_name || "—"}</p>
                      <p className="text-[10px] font-mono text-[#64748B]">{t.member_uid || ""}</p>
                    </td>
                    <td className="px-6 py-3 font-mono text-right text-emerald-400">+{fmtCoins(t.amount_minor)}</td>
                    <td className="px-6 py-3">
                      <span className="text-[10px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border border-emerald-400/30 text-emerald-400" data-testid={`admin-topup-status-${t.id.slice(-6)}`}>{t.status}</span>
                    </td>
                    <td className="px-6 py-3 text-[10px] font-mono text-[#64748B] hidden md:table-cell" title={t.session_id || ""} data-testid={`admin-topup-ref-${t.id.slice(-6)}`}>
                      {t.session_id ? `${t.session_id.slice(0, 14)}…` : "—"}
                    </td>
                    <td className="px-6 py-3 text-[#94A8B8] whitespace-nowrap">{(t.paid_at || t.created_at) ? fmtMYT(t.paid_at || t.created_at) : "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {member && (
        <div className="card-lux overflow-hidden" data-testid="coin-ledger-section">
          <div className="px-6 pt-6"><p className="eyebrow-label">Ledger — {member.full_name} ({member.uid})</p></div>
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm" data-testid="coin-ledger-table">
              <thead>
                <tr className="border-b border-[#222530] text-left">
                  <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">When</th>
                  <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Kind</th>
                  <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Amount</th>
                  <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Balance After</th>
                  <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Reason</th>
                  <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#222530]">
                {!ledger ? (
                  <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]">Loading ledger...</td></tr>
                ) : ledger.transactions.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]" data-testid="coin-ledger-empty">No coin transactions yet.</td></tr>
                ) : (
                  ledger.transactions.map((t) => (
                    <tr key={t.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`coin-tx-${t.id.slice(-6)}`}>
                      <td className="px-6 py-3 text-[#94A3B8] whitespace-nowrap">{t.created_at ? fmtMYT(t.created_at) : "—"}</td>
                      <td className="px-6 py-3">
                        <span className="text-[10px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border border-[#222530] text-[#94A3B8]">{t.kind}</span>
                      </td>
                      <td className={`px-6 py-3 font-mono text-right ${t.amount_minor >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {t.amount_minor >= 0 ? "+" : ""}{fmtCoins(t.amount_minor)}
                      </td>
                      <td className="px-6 py-3 font-mono text-white text-right">{fmtCoins(t.balance_after_minor)}</td>
                      <td className="px-6 py-3 text-xs text-[#94A3B8] max-w-[220px] truncate">{t.reason}</td>
                      <td className="px-6 py-3 text-xs text-[#64748B] hidden md:table-cell">{t.created_by?.name || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
